import {
    useEffect,
    useState,
} from 'react';

// FNP-918: module-scoped filter store (checklist §7; FNP-815).
//
// Filters live here rather than in component state so the FilterDrawer and the
// table filter the SAME list. Keyword and filters COMPOSE — they are applied
// independently in useInvoices, so applying both narrows rather than resets.
//
// ⚠️ These filters are applied CLIENT-SIDE, over the pages loaded so far.
// `listInvoices`'s `InvoicesFilterInput` is used for the ONE server-side
// narrowing this module needs (customerId, on the customer detail tab); the
// drawer's status/paid choices are not fields that input accepts, so they are
// matched in the browser. See the comment in useInvoices.ts.
export type InvoicesFilters = Record<string, string>;

let globalFilters: InvoicesFilters = {};
const filterListeners: Set<(filters: InvoicesFilters) => void> = new Set();

const notifyFilterChange = (filters: InvoicesFilters) => {
    globalFilters = filters;
    filterListeners.forEach(listener => listener(filters));
};

/** Test seam: resets the store so filters do not leak between test cases. */
export const resetInvoicesFilter = () => notifyFilterChange({});

export const useInvoicesFilter = () => {
    const [filters, setFilters] = useState<InvoicesFilters>(globalFilters);

    useEffect(() => {
        const listener = (newFilters: InvoicesFilters) => setFilters(newFilters);
        filterListeners.add(listener);
        return () => {
            filterListeners.delete(listener);
        };
    }, []);

    // The badge on FilterRecordsButton — a filter you cannot see is a filter you
    // forget is on (§7). Empty-string values are treated as unset so a cleared
    // field does not keep inflating the count.
    const activeFilterCount = Object.values(filters)
                                    .filter(value => value !== '' && value !== undefined)
                                    .length;

    return {
        filters,
        activeFilterCount,
        handleSetFilters: notifyFilterChange,
        handleClearFilters: () => notifyFilterChange({}),
    };
};
